import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import { useStudentUpdateRequest } from '../../hooks/useStudentUpdateRequest';

const StudentUpdateRequestsPage: React.FC = () => {
  const { useStudentUpdateRequests } = useStudentUpdateRequest();
  const { data, isLoading, error } = useStudentUpdateRequests();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Student Update Requests
      </Typography>
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load requests</Alert>
      ) : !data || data.length === 0 ? (
        <Alert severity="info">No update requests found</Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Updated Fields</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Requested At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((req: any) => {
                const changes = req.changes || [];
                const isExpanded = expandedId === req.id;
                return (
                  <React.Fragment key={req.id}>
                    <TableRow
                      hover
                      sx={{ cursor: changes.length > 0 ? 'pointer' : 'default' }}
                      onClick={() => setExpandedId(isExpanded ? null : req.id)}
                    >
                      <TableCell>{req.studentId ? `Student #${req.studentId}` : '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="primary" sx={{ textDecoration: 'underline', display: 'inline' }}>
                          {changes.length} field{changes.length !== 1 ? 's' : ''} changed
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.status || 'PENDING'}
                          color={
                            req.status === 'APPROVED'
                              ? 'success'
                              : req.status === 'REJECTED'
                              ? 'error'
                              : 'warning'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {req.requestedAt ? new Date(req.requestedAt).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <Box sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                            {changes.map((change: any) => (
                              <Box key={change.fieldName} sx={{ display: 'flex', gap: 2, mb: 0.5, fontSize: '0.875rem' }}>
                                <span style={{ fontWeight: 600, minWidth: 140 }}>{change.fieldName}</span>
                                <span style={{ color: '#c62828' }}>{change.oldValue || '-'}</span>
                                <span>→</span>
                                <span style={{ color: '#2e7d32' }}>{change.newValue || '-'}</span>
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default StudentUpdateRequestsPage;
